/* eslint-disable react/no-children-prop */
import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  Flex,
  Group,
  Stack,
  SimpleGrid,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useAppForm } from "@components/form/app-form";
import { US_STATES } from "@utils/data";

import { useSpringEnrollMutation } from "src/backendApi/queries/spring-health-queries";

import PhoneInput from "react-phone-number-input/input";

const Route = getRouteApi("/spring-health/register");

export function SpringHealthRegisterPage() {
  const { t } = useTranslation();
  const { mutateAsync } = useSpringEnrollMutation({});

  const { spring_id: sid, spring_guardian_id: gid } = Route.useSearch();

  const form = useAppForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      emailOptIn: true,
      phoneNum: "",
      smsOptIn: false,
      //
      addr1: "",
      addr2: "",
      city: "",
      provinceCode: "",
      zip: "",
      country: "US",
    },
    onSubmit: async ({ value }) => {
      console.log("SUBMITTED - HERE IS THE VALUE", value);
      await mutateAsync({
        payload: {
          spring_id: sid,
          spring_guardian_id: gid,
          user: {
            first_name: value.firstName,
            last_name: value.lastName,
            email: value.email,
            phonenumber: value.phoneNum,
            //
            shipping_information: {
              address_1: value.addr1,
              address_2: value.addr2,
              city: value.city,
              state: value.provinceCode,
              country: value.country,
              zipcode: value.zip,
            },
            notification_preferences: {
              is_email_ok: value.emailOptIn,
              is_sms_ok: value.smsOptIn,
            },
            //
          },
        },
      });
    },
  });

  return (
    <Stack align="flex-start">
      <Title order={3}>{t("springHealth.register.pageTitle")}</Title>
      <Text>{t("springHealth.register.pageDescription")}</Text>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <SimpleGrid cols={{ xs: 1, md: 2 }} spacing="xl">
          <Stack gap="xs">
            <Title order={4}>{t("springHealth.register.contact.title")}</Title>
            <Group>
              <form.AppField
                name="firstName"
                children={(field) => {
                  return (
                    <field.TextField
                      label={t("springHealth.register.contact.firstName")}
                      errors={field.state.meta.errors}
                      autoComplete="given-name"
                    />
                  );
                }}
              />

              <form.AppField
                name="lastName"
                children={(field) => {
                  return (
                    <field.TextField
                      label={t("springHealth.register.contact.lastName")}
                      errors={field.state.meta.errors}
                      autoComplete="family-name"
                    />
                  );
                }}
              />
            </Group>

            <form.AppField
              name="email"
              children={(field) => {
                return (
                  <field.TextField
                    label={t("springHealth.register.contact.emailAddress")}
                    errors={field.state.meta.errors}
                    autoComplete="email"
                  />
                );
              }}
            />
            <form.AppField
              name="emailOptIn"
              children={(field) => {
                return (
                  <field.Checkbox
                    label={t("springHealth.register.contact.emailOptIn")}
                  />
                );
              }}
            />

            <form.AppField
              name="phoneNum"
              children={(field) => {
                return (
                  <PhoneInput
                    onChange={(value) => {
                      if (value) {
                        field.handleChange(value);
                      }
                    }}
                    inputComponent={TextInput}
                    // TODO: allow different countries lol. default country select was weird in UI
                    country="US"
                    international
                    withCountryCallingCode
                    //
                    value={field.state.value}
                    // {...form.getInputProps("phoneNumber")}
                    label={t("springHealth.register.contact.phoneNumber")}
                    // required
                    // disabled={isPending}
                    data-testid="member-create-phonenum"
                  />
                );
              }}
            />
            <form.AppField
              name="smsOptIn"
              children={(field) => {
                return (
                  <field.Checkbox
                    label={t("springHealth.register.contact.smsOptIn")}
                  />
                );
              }}
            />
          </Stack>

          <Stack gap="xs">
            <Title order={4}>{t("springHealth.register.shipping.title")}</Title>
            <form.AppField
              name="addr1"
              children={(field) => {
                return (
                  <field.TextField
                    label={t("springHealth.register.shipping.address")}
                    errors={field.state.meta.errors}
                    autoComplete="address-line1"
                  />
                );
              }}
            />
            <form.AppField
              name="addr2"
              children={(field) => {
                return (
                  <field.TextField
                    label={t("springHealth.register.shipping.addressLine2")}
                    errors={field.state.meta.errors}
                    autoComplete="address-line2"
                  />
                );
              }}
            />

            <Group>
              <form.AppField
                name="city"
                children={(field) => {
                  return (
                    <field.TextField
                      label={t("springHealth.register.shipping.city")}
                      errors={field.state.meta.errors}
                      autoComplete="address-level2"
                    />
                  );
                }}
              />
              <form.AppField
                name="provinceCode"
                children={(field) => {
                  return (
                    <field.Select
                      label={t("springHealth.register.shipping.state")}
                      data={Object.values(US_STATES)}
                      value={field.state.value}
                      onChange={(newVal) => field.handleChange(newVal)}
                      onBlur={field.handleBlur}
                      autoComplete="address-level2"
                    />
                  );
                }}
              />
            </Group>

            <form.AppField
              name="zip"
              children={(field) => {
                return (
                  <field.TextField
                    label={t("springHealth.register.shipping.zipCode")}
                    errors={field.state.meta.errors}
                    autoComplete="postal-code"
                  />
                );
              }}
            />

            <form.AppField
              name="country"
              children={(field) => {
                return (
                  <field.Select
                    label={t("springHealth.register.shipping.country")}
                    data={["US"]}
                    value={field.state.value}
                    onChange={(newVal) => field.handleChange(newVal)}
                    onBlur={field.handleBlur}
                    disabled
                  />
                );
              }}
            />
          </Stack>
        </SimpleGrid>

        <Flex justify="flex-end" mt="xl" pr="xl">
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isDefaultValue,
            ]}
            children={([canSubmit, isSubmitting, isDefaultValue]) => (
              <form.SubmitButton
                loading={isSubmitting}
                disabled={!canSubmit || isDefaultValue}
              >
                {t("springHealth.register.submitButton")}
              </form.SubmitButton>
            )}
          />
        </Flex>
      </form>
    </Stack>
  );
}
